import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getLocation, getLocationContainers, createContainer, updateLocation } from '../services/api';
import { ArrowLeft, Plus, Package, QrCode as QrCodeIcon, Edit2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const LocationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [editFormData, setEditFormData] = useState({ name: '', description: '' });

  const fetchData = async () => {
    try {
      const [locationRes, containersRes] = await Promise.all([
        getLocation(id),
        getLocationContainers(id)
      ]);
      setLocation(locationRes.data);
      setContainers(containersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location) {
      setEditFormData({
        name: location.name,
        description: location.description || ''
      });
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createContainer({
        ...formData,
        location_id: id,
        parent_container_id: null
      });
      setFormData({ name: '', description: '' });
      setShowForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating container:', error);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateLocation(id, editFormData);
      setShowEditForm(false);
      fetchData();
    } catch (error) {
      console.error('Error updating location:', error);
      alert('Failed to update location. Please try again.');
    }
  };

  if (loading) return <div className="container">Loading...</div>;
  if (!location) return <div className="container">Location not found</div>;

  return (
    <div className="container">
      <button onClick={() => navigate('/')} className="button button-secondary" style={{ marginBottom: '20px' }}>
        <ArrowLeft size={20} style={{ display: 'inline', marginRight: '5px' }} />
        Back
      </button>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>{location.name}</h1>
            {location.description && <p style={{ color: '#666', marginTop: '10px' }}>{location.description}</p>}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => setShowEditForm(!showEditForm)} className="button button-secondary">
              <Edit2 size={20} />
            </button>
            <button onClick={() => setShowQR(!showQR)} className="button button-secondary">
              <QrCodeIcon size={20} />
            </button>
          </div>
        </div>

        {showQR && (
          <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <QRCodeSVG value={location.id} size={200} />
            <p style={{ marginTop: '10px', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>
              {location.id}
            </p>
          </div>
        )}

        {showEditForm && (
          <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <h3>Edit Location</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label className="label">Name</label>
                <input
                  type="text"
                  className="input"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Description</label>
                <textarea
                  className="input"
                  rows="3"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="button button-success">
                  Save Changes
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowEditForm(false);
                    setEditFormData({
                      name: location.name,
                      description: location.description || ''
                    });
                  }} 
                  className="button button-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '20px' }}>
        <h2>Containers</h2>
        <button onClick={() => setShowForm(!showForm)} className="button">
          <Plus size={20} style={{ display: 'inline', marginRight: '5px' }} />
          Add Container
        </button>
      </div>

      {showForm && (
        <div className="card">
          <h3>New Container</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">Name</label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Description</label>
              <textarea
                className="input"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="button">Create</button>
              <button type="button" onClick={() => setShowForm(false)} className="button button-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid">
        {containers.map((container) => (
          <div
            key={container.id}
            className="list-item"
            onClick={() => navigate(`/containers/${container.id}`)}
          >
            <h3>
              <Package size={20} style={{ display: 'inline', marginRight: '8px' }} />
              {container.name}
            </h3>
            {container.description && (
              <p style={{ color: '#666', marginTop: '8px' }}>{container.description}</p>
            )}
          </div>
        ))}
      </div>

      {containers.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: '#666' }}>
          <p>No containers yet. Add one to get started!</p>
        </div>
      )}
    </div>
  );
};

export default LocationDetail;
